import BaseWidgetComponent, {
  HtmlElementSize,
  HtmlElementPosition,
} from './BaseWidget.component';
import { getLocalStorage, setLocalStorage } from '../../../../../utils/storage';
import { WidgetKey } from '../../Dashboard.types';

interface BaseWidgetWithLocalStoragePersistenceProps {
  widgetKey: string;
  header: React.ReactNode | string;
  children?: React.ReactNode;
}

interface WidgetMetadata {
  size?: HtmlElementSize;
  position?: HtmlElementPosition;
}

function getInitialPosition(axis: keyof HtmlElementPosition, pixelStep: number) {
  const widgetKeys = Object.values(WidgetKey);

  const axisCoordinates = widgetKeys.reduce((acc, current) => {
    const data = getLocalStorage(current);

    if (data) {
      const widgetMetadata = JSON.parse(data) as WidgetMetadata;
      const axisCoordinate = widgetMetadata?.position?.[axis];

      if (typeof axisCoordinate === 'number') {
        acc.push(axisCoordinate)
      }
    }

    return acc;
  }, [] as number[])

  const maxAxisCoordinate = Math.max(...axisCoordinates);

  return axisCoordinates.length === 0 ? 0 : maxAxisCoordinate + pixelStep;
}

function persistWidgetPosition(
  widgetKey: string,
  newPosition: HtmlElementPosition,
) {
  const persistedMetadata = getLocalStorage(widgetKey);
  let newMetadata: WidgetMetadata = { position: newPosition };

  if (!!persistedMetadata && persistedMetadata.length > 0) {
    const { size } = JSON.parse(persistedMetadata);

    if (size) {
      newMetadata = { ...newMetadata, size: size };
    }
  }

  setLocalStorage(widgetKey, JSON.stringify(newMetadata));
}

function persistWidgetSize(widgetKey: string, newSize: HtmlElementSize) {
  const persistedMetadata = getLocalStorage(widgetKey);
  let newMetadata: WidgetMetadata = { size: newSize };

  if (!!persistedMetadata && persistedMetadata.length > 0) {
    const { position } = JSON.parse(persistedMetadata);

    if (position) {
      newMetadata = { ...newMetadata, position: position };
    }
  }

  setLocalStorage(widgetKey, JSON.stringify(newMetadata));
}

function getWidgetSize(widgetKey: string): HtmlElementSize | undefined {
  const persistedMetadata = getLocalStorage(widgetKey);

  if (!!persistedMetadata && persistedMetadata.length > 0) {
    const { size } = JSON.parse(persistedMetadata);
    return size;
  }
}

function getWidgetPosition(widgetKey: string): HtmlElementPosition | undefined {
  const persistedMetadata = getLocalStorage(widgetKey);

  if (!!persistedMetadata && persistedMetadata.length > 0) {
    const { position } = JSON.parse(persistedMetadata);
    return position;
  } else {
    const position = {
      x: getInitialPosition('x', 50),
      y: getInitialPosition('y', 25),
    };

    persistWidgetPosition(widgetKey, position);

    return position;
  }
}

export default function BaseWidgetWithLocalStoragePersistence({
  widgetKey,
  header,
  children,
}: BaseWidgetWithLocalStoragePersistenceProps) {
  const persistedWidgetPosition = getWidgetPosition(widgetKey);
  const persistedWidgetSize = getWidgetSize(widgetKey);

  function onPositionChange(newPosition: HtmlElementPosition) {
    persistWidgetPosition(widgetKey, newPosition);
  }

  function onSizeChange(newSize: HtmlElementSize) {
    persistWidgetSize(widgetKey, newSize);
  }

  return (
    <BaseWidgetComponent
      header={header}
      initialSize={persistedWidgetSize}
      initialPosition={persistedWidgetPosition}
      onPositionChange={onPositionChange}
      onSizeChange={onSizeChange}
    >
      {children}
    </BaseWidgetComponent>
  );
}
